import { SignInExpiredError } from "../errors";
import type { IdentityApi, Session } from "../types";
import { ACTIVE, ACTIVITY_HEADER, IDLE, type ActivityTracker } from "./activity";
import { SignedOutError } from "./errors";
import type { Http } from "./http";
import { ROUTES, signInUrl } from "./routes";
import { parseSession } from "./schemas";
import type { SessionSignals } from "./signals";

/** What the live client's parts share: the HTTP layer, the tab signals and the latest session seen. */
export interface LiveCtx {
  http: Http;
  signals: SessionSignals;
  activity: ActivityTracker;
  /** Keeps the latest session, for calls about its organisation. */
  remember(session: Session | null): Session | null;
  latest(): Session | null | undefined;
}

type AuthApi = Pick<
  IdentityApi,
  | "startSignIn"
  | "completeSignIn"
  | "getSession"
  | "switchOrganisation"
  | "signOut"
  | "acceptTerms"
  | "completeWorkspaceChoice"
>;

/**
 * Sign-in and the session against the identity service (D-026; FR-ADMIN-001, 014, 047). Sign-in leaves the app: the
 * browser goes to `/auth/sign-in`, AuthKit's hosted page, then `/auth/callback` on the service, which sets the
 * session cookie and comes back to `returnTo`.
 */
export function createAuthApi(ctx: LiveCtx): AuthApi {
  const { http, signals } = ctx;
  const changed = (session: Session): Session => {
    ctx.remember(session);
    signals.announce();
    return session;
  };

  return {
    startSignIn(returnTo, options) {
      return Promise.resolve(signInUrl(returnTo, options));
    },

    // The service finishes sign-in at /auth/callback itself and redirects; the app's callback page only runs if
    // /auth is not routed to the service, so this sign-in cannot be finished here.
    completeSignIn() {
      return Promise.reject(new SignInExpiredError());
    },

    async getSession() {
      // Only the person's own input moves the idle timeout, not this check (FR-ADMIN-047).
      const activity = { [ACTIVITY_HEADER]: ctx.activity.take() ? ACTIVE : IDLE };
      try {
        return ctx.remember(await http.get(ROUTES.me, parseSession, activity));
      } catch (error) {
        if (error instanceof SignedOutError) return ctx.remember(null);
        throw error;
      }
    },

    async switchOrganisation(organisationId) {
      return changed(
        await http.send("POST", ROUTES.switchOrganisation, { organisationId }, parseSession),
      );
    },

    async signOut() {
      await http.sendOnly("POST", ROUTES.signOut, {});
      ctx.remember(null);
      signals.announce();
    },

    async acceptTerms(version, ageConfirmed) {
      return changed(
        await http.send("POST", ROUTES.terms, { version, ageConfirmed }, parseSession),
      );
    },

    async completeWorkspaceChoice() {
      return changed(await http.send("POST", ROUTES.workspaceChoice, {}, parseSession));
    },
  };
}
